import React, { useEffect, useMemo, useState } from "react";
import {
  isoDate,
  weekStartISO,
  getWeekNumber,
  prettifyStoreName,
} from "./workshopCapacityUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const INVOICE_STORES = ["sport1royken", "sport1slemmestad"];

const MONTH_NAMES = [
  "Januar", "Februar", "Mars", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Desember",
];

function fmtKr(amount) {
  return amount > 0 ? `${Math.round(amount).toLocaleString("nb-NO")} kr` : "—";
}

// Backend gir deleromsetning per dag (dato = første gang servicen ble avsluttet),
// så hver måned får bare sine egne dager, også når en uke går over to måneder.
function getMonthForDate(dato) {
  return Number(dato.slice(5, 7));
}

function getYearForDate(dato) {
  return Number(dato.slice(0, 4));
}

function kortDato(dato) {
  return `${dato.slice(8, 10)}.${dato.slice(5, 7)}`;
}

function MonthDetailModal({ open, onClose, monthName, year, weekRows }) {
  if (!open) return null;

  const totals = weekRows.reduce(
    (acc, row) => {
      for (const slug of INVOICE_STORES) {
        const d = row.storeData[slug] || {};
        acc[slug].delerOmsetning += d.delerOmsetning || 0;
        acc[slug].delerGarantiVerdi += d.delerGarantiVerdi || 0;
        acc[slug].delerAntallLinjer += d.delerAntallLinjer || 0;
      }
      acc.totalDelerOmsetning += row.totalDelerOmsetning;
      acc.totalDelerGarantiVerdi += row.totalDelerGarantiVerdi;
      acc.totalDelerAntallLinjer += row.totalDelerAntallLinjer;
      return acc;
    },
    {
      sport1royken: { delerOmsetning: 0, delerGarantiVerdi: 0, delerAntallLinjer: 0 },
      sport1slemmestad: { delerOmsetning: 0, delerGarantiVerdi: 0, delerAntallLinjer: 0 },
      totalDelerOmsetning: 0,
      totalDelerGarantiVerdi: 0,
      totalDelerAntallLinjer: 0,
    }
  );

  const hasGaranti = totals.totalDelerGarantiVerdi > 0 ||
    weekRows.some((r) => r.totalDelerGarantiVerdi > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-5xl rounded-lg bg-white shadow-xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between border-b px-5 py-4 bg-gray-50">
          <div>
            <h3 className="text-lg font-semibold">{monthName} {year} – Ukeoversikt deler</h3>
            <div className="text-sm text-gray-500">{weekRows.length} uke{weekRows.length !== 1 ? "r" : ""}</div>
          </div>
          <button onClick={onClose} className="rounded border px-3 py-1.5 text-sm hover:bg-gray-100">
            Lukk
          </button>
        </div>

        <div className="overflow-auto max-h-[75vh] p-4 space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
              Deler- og tilbehørsomsetning
            </h4>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Uke</th>
                  <th className="px-3 py-2 text-left">Dager</th>
                  {INVOICE_STORES.map((slug) => (
                    <React.Fragment key={slug}>
                      <th className="px-3 py-2 text-right">
                        {prettifyStoreName(slug)}<br />
                        <span className="font-normal text-gray-500">Linjer</span>
                      </th>
                      <th className="px-3 py-2 text-right">
                        {prettifyStoreName(slug)}<br />
                        <span className="font-normal text-gray-500">Omsetning</span>
                      </th>
                    </React.Fragment>
                  ))}
                  <th className="px-3 py-2 text-right">Total linjer</th>
                  <th className="px-3 py-2 text-right">Total omsetning</th>
                </tr>
              </thead>
              <tbody>
                {weekRows.map((row) => (
                  <tr key={row.weekIso} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">Uke {row.weekNo}</td>
                    <td className="px-3 py-2 text-gray-500">{row.periode}</td>
                    {INVOICE_STORES.map((slug) => {
                      const d = row.storeData[slug] || {};
                      return (
                        <React.Fragment key={slug}>
                          <td className="px-3 py-2 text-right text-gray-700">
                            {d.delerAntallLinjer > 0 ? d.delerAntallLinjer : "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {fmtKr(d.delerOmsetning || 0)}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td className="px-3 py-2 text-right text-gray-700">
                      {row.totalDelerAntallLinjer > 0 ? row.totalDelerAntallLinjer : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-bold">
                      {fmtKr(row.totalDelerOmsetning)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                <tr>
                  <td colSpan={2} className="px-3 py-2">Total</td>
                  {INVOICE_STORES.map((slug) => {
                    const t = totals[slug];
                    return (
                      <React.Fragment key={slug}>
                        <td className="px-3 py-2 text-right">{t.delerAntallLinjer || "—"}</td>
                        <td className="px-3 py-2 text-right">{fmtKr(t.delerOmsetning)}</td>
                      </React.Fragment>
                    );
                  })}
                  <td className="px-3 py-2 text-right">{totals.totalDelerAntallLinjer || "—"}</td>
                  <td className="px-3 py-2 text-right">{fmtKr(totals.totalDelerOmsetning)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {hasGaranti && (
            <div>
              <h4 className="text-sm font-semibold text-orange-700 mb-2 uppercase tracking-wide">
                Garanti/reklamasjon deler (ikke fakturerbart)
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-orange-50 text-orange-800">
                    <tr>
                      <th className="px-3 py-2 text-left">Uke</th>
                      <th className="px-3 py-2 text-left">Dager</th>
                      {INVOICE_STORES.map((slug) => (
                        <th key={slug} className="px-3 py-2 text-right">
                          {prettifyStoreName(slug)}<br />
                          <span className="font-normal text-orange-600">Verdi</span>
                        </th>
                      ))}
                      <th className="px-3 py-2 text-right">Total verdi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekRows.map((row) => (
                      <tr key={row.weekIso} className="border-t hover:bg-orange-50">
                        <td className="px-3 py-2 font-medium">Uke {row.weekNo}</td>
                        <td className="px-3 py-2 text-gray-500">{row.periode}</td>
                        {INVOICE_STORES.map((slug) => {
                          const v = row.storeData[slug]?.delerGarantiVerdi || 0;
                          return (
                            <td key={slug} className="px-3 py-2 text-right text-orange-800 font-medium">
                              {v > 0 ? fmtKr(v) : "—"}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right font-bold text-orange-800">
                          {row.totalDelerGarantiVerdi > 0 ? fmtKr(row.totalDelerGarantiVerdi) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-orange-200 bg-orange-50 font-semibold">
                    <tr>
                      <td colSpan={2} className="px-3 py-2">Total</td>
                      {INVOICE_STORES.map((slug) => (
                        <td key={slug} className="px-3 py-2 text-right">
                          {fmtKr(totals[slug]?.delerGarantiVerdi || 0)}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right">{fmtKr(totals.totalDelerGarantiVerdi)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function WorkshopDelerHistoryTab() {
  const today = useMemo(() => new Date(), []);
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  const prevMonth = today.getMonth() === 0 ? 12 : today.getMonth();
  const prevMonthYear = today.getMonth() === 0 ? currentYear - 1 : currentYear;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  const [dataByStore, setDataByStore] = useState({});
  const [modal, setModal] = useState({ open: false, monthName: "", year: 0, weekRows: [] });
  const [billingMonth, setBillingMonth] = useState(prevMonth);
  const [billingYear, setBillingYear] = useState(prevMonthYear);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= 2023; y--) years.push(y);
    return years;
  }, [currentYear]);

  const { fromISO, toISO } = useMemo(() => {
    const lastDayOfYear = new Date(selectedYear, 11, 31);
    const to = lastDayOfYear > today ? today : lastDayOfYear;
    return {
      fromISO: `${selectedYear}-01-01`,
      toISO: isoDate(to),
    };
  }, [selectedYear, today]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const responses = await Promise.all(
          INVOICE_STORES.map((slug) =>
            fetch(
              `${API_BASE_URL}/workshop/capacity-history?verkstedSlug=${encodeURIComponent(slug)}&from=${fromISO}&to=${toISO}`
            ).then((r) => (r.ok ? r.json() : { perDag: [] }))
          )
        );

        const next = {};
        INVOICE_STORES.forEach((slug, i) => {
          next[slug] = responses[i].perDag || [];
        });
        setDataByStore(next);
      } catch (err) {
        console.error("Feil ved henting av deleromsetning:", err);
        setDataByStore({});
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [fromISO, toISO]);

  const monthlyData = useMemo(() => {
    const maxMonth = selectedYear === currentYear ? currentMonth : 12;
    const months = [];

    for (let m = 1; m <= maxMonth; m++) {
      const storeData = {};
      let totalDelerOmsetning = 0;
      let totalDelerGarantiVerdi = 0;
      let totalDelerAntallLinjer = 0;

      for (const slug of INVOICE_STORES) {
        const rows = dataByStore[slug] || [];
        let delerOmsetning = 0;
        let delerGarantiVerdi = 0;
        let delerAntallLinjer = 0;

        for (const row of rows) {
          if (getYearForDate(row.dato) === selectedYear && getMonthForDate(row.dato) === m) {
            delerOmsetning += Number(row.delerOmsetning || 0);
            delerGarantiVerdi += Number(row.delerGarantiVerdi || 0);
            delerAntallLinjer += Number(row.delerAntallLinjer || 0);
          }
        }

        storeData[slug] = { delerOmsetning, delerGarantiVerdi, delerAntallLinjer };
        totalDelerOmsetning += delerOmsetning;
        totalDelerGarantiVerdi += delerGarantiVerdi;
        totalDelerAntallLinjer += delerAntallLinjer;
      }

      months.push({
        month: m,
        monthName: MONTH_NAMES[m - 1],
        storeData,
        totalDelerOmsetning,
        totalDelerGarantiVerdi,
        totalDelerAntallLinjer,
      });
    }

    return months;
  }, [dataByStore, selectedYear, currentYear, currentMonth]);

  const yearTotals = useMemo(() => {
    const storeTotals = Object.fromEntries(
      INVOICE_STORES.map((s) => [s, { delerOmsetning: 0, delerGarantiVerdi: 0, delerAntallLinjer: 0 }])
    );
    let totalDelerOmsetning = 0;
    let totalDelerGarantiVerdi = 0;
    let totalDelerAntallLinjer = 0;

    for (const row of monthlyData) {
      for (const slug of INVOICE_STORES) {
        storeTotals[slug].delerOmsetning += row.storeData[slug]?.delerOmsetning || 0;
        storeTotals[slug].delerGarantiVerdi += row.storeData[slug]?.delerGarantiVerdi || 0;
        storeTotals[slug].delerAntallLinjer += row.storeData[slug]?.delerAntallLinjer || 0;
      }
      totalDelerOmsetning += row.totalDelerOmsetning;
      totalDelerGarantiVerdi += row.totalDelerGarantiVerdi;
      totalDelerAntallLinjer += row.totalDelerAntallLinjer;
    }

    return { storeTotals, totalDelerOmsetning, totalDelerGarantiVerdi, totalDelerAntallLinjer };
  }, [monthlyData]);

  const hasGaranti = yearTotals.totalDelerGarantiVerdi > 0;

  const billingTotals = useMemo(() => {
    const storeTotals = Object.fromEntries(
      INVOICE_STORES.map((s) => [s, { delerOmsetning: 0, delerGarantiVerdi: 0 }])
    );
    let totalDelerOmsetning = 0;
    let totalDelerGarantiVerdi = 0;

    for (const slug of INVOICE_STORES) {
      for (const row of dataByStore[slug] || []) {
        if (getYearForDate(row.dato) === billingYear && getMonthForDate(row.dato) === billingMonth) {
          storeTotals[slug].delerOmsetning += Number(row.delerOmsetning || 0);
          storeTotals[slug].delerGarantiVerdi += Number(row.delerGarantiVerdi || 0);
        }
      }
      totalDelerOmsetning += storeTotals[slug].delerOmsetning;
      totalDelerGarantiVerdi += storeTotals[slug].delerGarantiVerdi;
    }

    return { storeTotals, totalDelerOmsetning, totalDelerGarantiVerdi };
  }, [dataByStore, billingMonth, billingYear]);

  function openMonthModal(monthRow) {
    // Dagene i måneden, gruppert per uke. Uker som går over to måneder vises bare
    // med dagene som hører til denne måneden.
    const perUke = {};
    for (const slug of INVOICE_STORES) {
      for (const row of dataByStore[slug] || []) {
        if (getYearForDate(row.dato) !== selectedYear || getMonthForDate(row.dato) !== monthRow.month) continue;
        const weekIso = weekStartISO(new Date(row.dato + "T00:00:00"));
        const uke = (perUke[weekIso] ||= {
          weekIso,
          weekNo: getWeekNumber(new Date(weekIso + "T00:00:00")),
          fra: row.dato,
          til: row.dato,
          storeData: Object.fromEntries(
            INVOICE_STORES.map((s) => [s, { delerOmsetning: 0, delerGarantiVerdi: 0, delerAntallLinjer: 0 }])
          ),
          totalDelerOmsetning: 0,
          totalDelerGarantiVerdi: 0,
          totalDelerAntallLinjer: 0,
        });
        if (row.dato < uke.fra) uke.fra = row.dato;
        if (row.dato > uke.til) uke.til = row.dato;
        const d = uke.storeData[slug];
        d.delerOmsetning += Number(row.delerOmsetning || 0);
        d.delerGarantiVerdi += Number(row.delerGarantiVerdi || 0);
        d.delerAntallLinjer += Number(row.delerAntallLinjer || 0);
        uke.totalDelerOmsetning += Number(row.delerOmsetning || 0);
        uke.totalDelerGarantiVerdi += Number(row.delerGarantiVerdi || 0);
        uke.totalDelerAntallLinjer += Number(row.delerAntallLinjer || 0);
      }
    }

    const weekRows = Object.values(perUke)
      .sort((a, b) => a.weekIso.localeCompare(b.weekIso))
      .map((uke) => ({
        ...uke,
        periode: uke.fra === uke.til ? kortDato(uke.fra) : `${kortDato(uke.fra)}–${kortDato(uke.til)}`,
      }));

    setModal({
      open: true,
      monthName: monthRow.monthName,
      year: selectedYear,
      weekRows,
    });
  }

  if (loading) {
    return <div className="py-10 text-center">Laster deleroversikt…</div>;
  }

  return (
    <div className="space-y-6">
      <MonthDetailModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        monthName={modal.monthName}
        year={modal.year}
        weekRows={modal.weekRows}
      />

      <div className="rounded border bg-green-50 p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xl font-bold">Faktureringsgrunnlag – Deler og tilbehør</div>
            <div className="text-sm text-gray-700 mt-1">
              Omsetning fra deler og tilbehør lagt til på service, per måned for Sport 1 Røyken og Sport 1 Slemmestad. Delene telles i måneden servicen ble avsluttet. Klikk på en måned for ukeoversikt.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">År:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="border rounded px-2 py-1.5 text-sm bg-white"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Sammendragskort */}
      <div className="grid md:grid-cols-3 gap-4">
        {INVOICE_STORES.map((slug) => {
          const t = yearTotals.storeTotals[slug];
          return (
            <div key={slug} className="rounded border p-4 bg-white">
              <div className="text-sm text-gray-600">{prettifyStoreName(slug)}</div>
              <div className="text-xl font-bold mt-1">{fmtKr(t?.delerOmsetning || 0)}</div>
              <div className="text-sm text-gray-500">{t?.delerAntallLinjer || 0} delelinjer</div>
              {(t?.delerGarantiVerdi || 0) > 0 && (
                <div className="text-sm text-orange-600 mt-0.5">
                  + {fmtKr(t.delerGarantiVerdi)} garanti/reklamasjon
                </div>
              )}
            </div>
          );
        })}
        <div className="rounded border p-4 bg-green-100">
          <div className="text-sm text-gray-600">Totalt {selectedYear}</div>
          <div className="text-xl font-bold mt-1">{fmtKr(yearTotals.totalDelerOmsetning)}</div>
          <div className="text-sm text-gray-500">{yearTotals.totalDelerAntallLinjer} delelinjer</div>
          {yearTotals.totalDelerGarantiVerdi > 0 && (
            <div className="text-sm text-orange-600 mt-0.5">
              + {fmtKr(yearTotals.totalDelerGarantiVerdi)} garanti/reklamasjon
            </div>
          )}
        </div>
      </div>

      {/* Deleromsetning-tabell */}
      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-2">Deler- og tilbehørsomsetning</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full border rounded bg-white">
            <thead className="bg-gray-100 text-sm text-gray-700">
              <tr>
                <th className="px-4 py-2 text-left">Måned</th>
                {INVOICE_STORES.map((slug) => (
                  <React.Fragment key={slug}>
                    <th className="px-4 py-2 text-right">
                      {prettifyStoreName(slug)}<br />
                      <span className="font-normal text-gray-500">Linjer</span>
                    </th>
                    <th className="px-4 py-2 text-right">
                      {prettifyStoreName(slug)}<br />
                      <span className="font-normal text-gray-500">Omsetning</span>
                    </th>
                  </React.Fragment>
                ))}
                <th className="px-4 py-2 text-right">Total linjer</th>
                <th className="px-4 py-2 text-right">Total omsetning</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData.map((row) => (
                <tr
                  key={row.month}
                  className="border-t cursor-pointer hover:bg-green-50"
                  onClick={() => openMonthModal(row)}
                  title="Klikk for ukeoversikt"
                >
                  <td className="px-4 py-2 font-medium text-green-700 underline underline-offset-2">
                    {row.monthName} {selectedYear}
                  </td>
                  {INVOICE_STORES.map((slug) => {
                    const d = row.storeData[slug] || {};
                    return (
                      <React.Fragment key={slug}>
                        <td className="px-4 py-2 text-right text-gray-700">
                          {d.delerAntallLinjer > 0 ? d.delerAntallLinjer : "—"}
                        </td>
                        <td className="px-4 py-2 text-right font-medium">
                          {fmtKr(d.delerOmsetning || 0)}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td className="px-4 py-2 text-right text-gray-700">
                    {row.totalDelerAntallLinjer > 0 ? row.totalDelerAntallLinjer : "—"}
                  </td>
                  <td className="px-4 py-2 text-right font-bold">
                    {fmtKr(row.totalDelerOmsetning)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-gray-300 bg-gray-50 font-semibold text-sm">
              <tr>
                <td className="px-4 py-2">Total {selectedYear}</td>
                {INVOICE_STORES.map((slug) => {
                  const t = yearTotals.storeTotals[slug];
                  return (
                    <React.Fragment key={slug}>
                      <td className="px-4 py-2 text-right">{t?.delerAntallLinjer || "—"}</td>
                      <td className="px-4 py-2 text-right">{fmtKr(t?.delerOmsetning || 0)}</td>
                    </React.Fragment>
                  );
                })}
                <td className="px-4 py-2 text-right">{yearTotals.totalDelerAntallLinjer || "—"}</td>
                <td className="px-4 py-2 text-right">{fmtKr(yearTotals.totalDelerOmsetning)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Garanti/reklamasjon-tabell */}
      <div>
        <h3 className="text-base font-semibold text-orange-700 mb-1">
          Garanti/reklamasjon deler
        </h3>
        <p className="text-sm text-gray-500 mb-2">
          Deler levert under garanti eller reklamasjon. Regnes ikke som fakturerbar deleromsetning.
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full border rounded bg-white">
            <thead className="bg-orange-50 text-sm text-orange-800">
              <tr>
                <th className="px-4 py-2 text-left">Måned</th>
                {INVOICE_STORES.map((slug) => (
                  <th key={slug} className="px-4 py-2 text-right">
                    {prettifyStoreName(slug)}<br />
                    <span className="font-normal text-orange-600">Verdi</span>
                  </th>
                ))}
                <th className="px-4 py-2 text-right">Total verdi</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData.map((row) => (
                <tr
                  key={row.month}
                  className="border-t cursor-pointer hover:bg-orange-50"
                  onClick={() => openMonthModal(row)}
                  title="Klikk for ukeoversikt"
                >
                  <td className="px-4 py-2 font-medium text-green-700 underline underline-offset-2">
                    {row.monthName} {selectedYear}
                  </td>
                  {INVOICE_STORES.map((slug) => {
                    const v = row.storeData[slug]?.delerGarantiVerdi || 0;
                    return (
                      <td key={slug} className="px-4 py-2 text-right text-orange-800 font-medium">
                        {v > 0 ? fmtKr(v) : "—"}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2 text-right font-bold text-orange-800">
                    {row.totalDelerGarantiVerdi > 0 ? fmtKr(row.totalDelerGarantiVerdi) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-orange-200 bg-orange-50 font-semibold text-sm">
              <tr>
                <td className="px-4 py-2">Total {selectedYear}</td>
                {INVOICE_STORES.map((slug) => (
                  <td key={slug} className="px-4 py-2 text-right">
                    {fmtKr(yearTotals.storeTotals[slug]?.delerGarantiVerdi || 0)}
                  </td>
                ))}
                <td className="px-4 py-2 text-right">
                  {yearTotals.totalDelerGarantiVerdi > 0 ? fmtKr(yearTotals.totalDelerGarantiVerdi) : "—"}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        {!hasGaranti && (
          <div className="mt-2 text-sm text-gray-400 italic">
            Ingen garanti-/reklamasjonsdeler registrert i {selectedYear}.
          </div>
        )}
      </div>

      {/* Samlet faktureringsgrunnlag */}
      <div className="rounded border-2 border-green-300 bg-green-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="text-base font-bold text-green-900">
            Faktureringsgrunnlag deler – {MONTH_NAMES[billingMonth - 1]} {billingYear}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Måned:</label>
            <select
              value={billingMonth}
              onChange={(e) => setBillingMonth(Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm bg-white"
            >
              {MONTH_NAMES.map((name, i) => (
                <option key={i + 1} value={i + 1}>{name}</option>
              ))}
            </select>
            <select
              value={billingYear}
              onChange={(e) => setBillingYear(Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm bg-white"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-gray-600 border-b border-green-200">
              <tr>
                <th className="pb-2 text-left font-medium">Butikk</th>
                <th className="pb-2 text-right font-medium">Deleromsetning</th>
                <th className="pb-2 text-right font-medium">Garanti/reklamasjon</th>
                <th className="pb-2 text-right font-semibold">Total deleromsetning</th>
              </tr>
            </thead>
            <tbody>
              {INVOICE_STORES.map((slug) => {
                const t = billingTotals.storeTotals[slug];
                const deler = t?.delerOmsetning || 0;
                const garanti = t?.delerGarantiVerdi || 0;
                return (
                  <tr key={slug} className="border-t border-green-200">
                    <td className="py-2 font-medium">{prettifyStoreName(slug)}</td>
                    <td className="py-2 text-right">{fmtKr(deler)}</td>
                    <td className="py-2 text-right text-orange-700">
                      {garanti > 0 ? fmtKr(garanti) : "—"}
                    </td>
                    <td className="py-2 text-right font-bold text-green-900">{fmtKr(deler)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-green-400">
              <tr>
                <td className="pt-2 font-bold">Total</td>
                <td className="pt-2 text-right font-bold">{fmtKr(billingTotals.totalDelerOmsetning)}</td>
                <td className="pt-2 text-right font-bold text-orange-700">
                  {billingTotals.totalDelerGarantiVerdi > 0 ? fmtKr(billingTotals.totalDelerGarantiVerdi) : "—"}
                </td>
                <td className="pt-2 text-right font-bold text-lg text-green-900">
                  {fmtKr(billingTotals.totalDelerOmsetning)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
